import { useEffect, useRef } from 'react';

import { Button, GrayColors, typographyStyle, Input, Flex } from '@revenuemarket/cds';
import { ResponseStatus } from '@revenuemarket/http';
import { convert, mixpanelTrack } from '@revenuemarket/utils';
import { extractPartnerDomain } from '@revenuemarket/utils/partners/getPartnerByHostname';
import { Divider, Form, Typography, type InputRef } from 'antd';
import { Rule } from 'antd/es/form';
import { getCookie, removeCookies } from 'cookies-next';
import { useRouter } from 'next/router';
import * as yup from 'yup';

import { getDomainForCookie } from '~apis/axios/instance';
import GoogleOauthButton from '~components/Auth/Signin/GoogleLoginButton';
import KakaoLoginButton from '~components/Auth/Signin/KakaoLoginButton';
import NaverLoginButton from '~components/Auth/Signin/NaverLoginButton';
import { COMMON_YUP } from '~constants/yup-scheme';
import { useSendPasscode } from '~hooks/queries/auth/query';
import { css } from '~libs/emotion';
import { MIXPANEL_EVENT } from '~libs/mixpanel/event';

export type SignupEmailStepModel =
  | {
      email?: string;
      isOnOAuthFlow: boolean;
    }
  | undefined;

interface SignupEmailStepForm {
  email: string;
}

const SignupEmailStepScheme = yup.object({
  email: COMMON_YUP.email,
});

const getSchemeRule = (field: string): Rule => ({
  validator: async (_, value) => {
    await SignupEmailStepScheme.validateAt(field, { [field]: value });
  },
});

interface Props {
  model: SignupEmailStepModel;
  onFinish: (values: SignupEmailStepForm) => void;
  isPartnerIntro: boolean;
  host: string;
}

const SignupEmailStep = ({ model, onFinish: onFinishProp, isPartnerIntro, host }: Props) => {
  const router = useRouter();

  const [form] = Form.useForm<SignupEmailStepForm>();
  const { mutateAsync: sendPasscode, isLoading } = useSendPasscode();

  const inputRef = useRef<InputRef | null>(null);

  // 쿠키에서 이메일값을 읽어옴
  const email = getCookie('email-from-landing');

  useEffect(() => {
    if (email) {
      form.setFieldsValue({ email: email as string });
      form.submit();
      removeCookies('email-from-landing', {
        domain: getDomainForCookie(),
      });
    }
  }, [email, form]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const onFinish = async (values: SignupEmailStepForm) => {
    if (!values.email) {
      form.setFields([
        {
          name: 'email',
          errors: ['이메일을 입력해주세요.'],
        },
      ]);
      return;
    }
    mixpanelTrack(MIXPANEL_EVENT.USER.CLICK_SIGNUP_TRIGGER, {
      provider: 'email',
    });

    const partnerDomain = extractPartnerDomain(host);
    const response = await sendPasscode({
      ...values,
      partnerDomain,
    });
    if (response.Status === ResponseStatus.SUCCESS) {
      form.resetFields(['email']);
      onFinishProp(values);
    } else {
      switch (response.ErrorCode) {
        case 'DUPLICATE_LOGIN_INFO':
          form.setFields([
            {
              name: 'email',
              errors: ['이미 존재하는 계정입니다.'],
            },
          ]);
          break;
        default:
          break;
      }
    }
  };

  return (
    <Flex
      className={css`
        width: 100%;
        height: calc(100vh - 128px);
        @media (max-width: 768px) {
          flex-direction: column !important;
          align-items: flex-start !important;
          width: calc(100vw - 40px);
        }
      `}
      align="center"
      justify="center"
    >
      <Flex
        style={{
          flex: 1,
        }}
        justify="center"
        className={css`
          @media (max-width: 768px) {
            width: 100%;
          }
        `}
      >
        <Flex
          direction="column"
          gap={12}
          style={{
            width: 480,
          }}
          className={css`
            padding: 0px 40px;
            @media (max-width: 768px) {
              max-width: 440px;
              width: 100% !important;
              padding: 0px;
            }
          `}
        >
          <Flex
            direction="column"
            gap={8}
            style={{
              paddingBottom: 12,
            }}
          >
            <Typography.Text
              style={{
                ...typographyStyle['24_600'],
                color: GrayColors.gray900,
              }}
              className={css`
                @media (max-width: 768px) {
                  margin-top: 80px;
                }
              `}
            >
              {isPartnerIntro ? '회원가입하기' : '실시간 자금관리를 시작하세요'}
            </Typography.Text>
            {!isPartnerIntro && (
              <Typography.Text
                style={{
                  ...typographyStyle['16_400'],
                  color: GrayColors.gray600,
                }}
              >
                클로브는 법인사업자 전용 서비스입니다.
              </Typography.Text>
            )}
          </Flex>
          {!model?.isOnOAuthFlow && (
            <>
              <KakaoLoginButton
                title="카카오로 가입하기"
                onKakaoOauthClick={() => {
                  mixpanelTrack(MIXPANEL_EVENT.USER.CLICK_SIGNUP_TRIGGER, {
                    provider: 'kakao',
                  });
                }}
              />

              <NaverLoginButton
                title="네이버로 가입하기"
                onNaverOauthClick={() => {
                  mixpanelTrack(MIXPANEL_EVENT.USER.CLICK_SIGNUP_TRIGGER, {
                    provider: 'naver',
                  });
                }}
              />

              <GoogleOauthButton
                title="구글로 가입하기"
                onGoogleOauthClick={() => {
                  mixpanelTrack(MIXPANEL_EVENT.USER.CLICK_SIGNUP_TRIGGER, {
                    provider: 'google',
                  });
                }}
              />

              <Divider
                style={{
                  ...typographyStyle['16_400'],
                  color: GrayColors.gray800,
                  marginBottom: '12px',
                }}
              >
                또는
              </Divider>
            </>
          )}

          <Form
            form={form}
            onFinish={onFinish}
            initialValues={{ ...model, email: '' }}
            className={css`
              width: 400px;

              @media (max-width: 768px) {
                width: 100%;
              }
            `}
          >
            <Flex direction="column" gap={12}>
              <Form.Item
                name="email"
                rules={[getSchemeRule('email')]}
                style={{
                  marginBottom: '0',
                }}
                className={css`
                  &&& .ant-form-item-explain-error {
                    font-size: 14px;
                  }
                `}
              >
                <Input
                  ref={inputRef}
                  size="large"
                  inputSize="xlarge"
                  type="email"
                  placeholder="이메일을 입력해주세요"
                  onChange={(e) => {
                    form.setFields([
                      {
                        name: 'email',
                        errors: [],
                      },
                    ]);
                  }}
                />
              </Form.Item>

              <Form.Item noStyle dependencies={['email']}>
                {({ getFieldValue }) => {
                  const email = convert(getFieldValue('email'), String);
                  const valid = (() => {
                    try {
                      COMMON_YUP.email.validateSync(email);
                      return true;
                    } catch {
                      return false;
                    }
                  })();

                  return (
                    <Button
                      buttonType="primary"
                      buttonSize="xlarge"
                      loading={isLoading}
                      onClick={() => {
                        form.submit();
                      }}
                      style={{
                        width: '100%',
                      }}
                    >
                      이메일로 가입하기
                    </Button>
                  );
                }}
              </Form.Item>
            </Flex>
          </Form>

          {!model?.isOnOAuthFlow && (
            <Flex align="center" gap={8}>
              <Typography.Text
                style={{
                  ...typographyStyle['14_400'],
                  color: GrayColors.gray600,
                }}
              >
                이미 계정이 있으신가요?
              </Typography.Text>

              <Button
                buttonSize="default"
                buttonType="transparent"
                onClick={async () => {
                  await router.push('/auth/signin');
                }}
              >
                로그인
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SignupEmailStep;
